/**
 * Date formatting utilities
 */

/**
 * Formats a date string into a human-readable format
 * @param dateString - ISO date string or null/undefined
 * @returns Formatted date string
 */
export const formatDate = (dateString: string | null | undefined): string => {
  // Explicitly handle null/undefined vs empty string
  if (dateString === null || dateString === undefined) return 'Not specified';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';

    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  } catch (err) {
    return 'Invalid date';
  }
};

/**
 * Formats a date string into a short format (MM/DD/YYYY)
 * @param dateString - ISO date string or null/undefined
 * @returns Short formatted date string
 */
export const formatShortDate = (dateString: string | null | undefined): string => {
  // Explicitly handle null/undefined vs empty string
  if (dateString === null || dateString === undefined) return 'Not specified';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';

    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'UTC',
    }).format(date);
  } catch (err) {
    return 'Invalid date';
  }
};

/**
 * Formats a date string into MM/YYYY format (month/year only)
 * @param dateString - ISO date string or null/undefined
 * @returns Formatted month/year string
 */export const formatMonthYear = (dateString: string | null | undefined): string => {
  if (dateString === null || dateString === undefined || dateString === '') return '';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${month}/${year}`;
  } catch {
    return '';
  }
};

/**
 * Audit and retention timestamps are displayed in UTC rather than in any one region's local
 * time. CLEAR runs across chapters in several regions, so there is no single "local" zone a
 * shared audit trail could correctly be stamped with -- two admins comparing the same record
 * would otherwise read two different times for it. UTC is what the database stores
 * (timestamptz, migration 0117) and what the schedulers run on, so showing it keeps the
 * screen, the data and the logs in one vocabulary.
 *
 * The browser-local rendering is offered alongside it for readability, never instead of it.
 */

/** Formats an ISO timestamp in UTC, e.g. "Oct 5, 2026, 09:04:03 UTC". */
export const formatUtcTimestamp = (dateString: string | null | undefined): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';

  const formatted = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  }).format(date);

  return `${formatted} UTC`;
};

/**
 * Formats an ISO timestamp in the viewer's own timezone, including the zone abbreviation so
 * it is never mistaken for the UTC value sitting next to it.
 */
export const formatLocalTimestamp = (dateString: string | null | undefined): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  }).format(date);
};

/** True when the viewer is already on UTC, so the local line would just repeat the UTC one. */
export const viewerIsUtc = (): boolean => {
  try {
    return new Date().getTimezoneOffset() === 0;
  } catch {
    return false;
  }
};